import type { TFunction } from "i18next";
import type { Trip } from "../types";
import {
  getStatusTone,
  getStatusBadgeStyle,
  getStatusBadgeLabel,
} from "../../../../../../utils/getStatusEntity";
import type { Tone } from "../../../../../../components/ui";

export const TRIP_PRIORITY_TONES = {
  urgent: "error",
  high: "warning",
  medium: "warning",
  low: "success",
} as const satisfies Record<string, Tone>;

export const TRIP_PRIORITY_LABEL_KEYS = {
  urgent: "trips.priority.urgent",
  high: "trips.priority.high",
  medium: "trips.priority.medium",
  low: "trips.priority.low",
} as const satisfies Record<keyof typeof TRIP_PRIORITY_TONES, string>;

export const TRIP_ASSIGNMENT_STATUS_TONES = {
  active: "success",
  completed: "info",
  cancelled: "error",
} as const satisfies Record<string, Tone>;

const PRIORITY_TONE_LOOKUP: Record<string, Tone> = TRIP_PRIORITY_TONES;
const PRIORITY_LABEL_LOOKUP: Record<string, string> = TRIP_PRIORITY_LABEL_KEYS;
const ASSIGNMENT_STATUS_TONE_LOOKUP: Record<string, Tone> =
  TRIP_ASSIGNMENT_STATUS_TONES;

export function getAssignmentStatusTone(status?: string): Tone {
  return ASSIGNMENT_STATUS_TONE_LOOKUP[status ?? ""] ?? "warning";
}

export function getStatusToneForTrip(trip: Trip): Tone {
  return getStatusTone(trip.status);
}

export function getStatusStyle(
  trip: Trip,
): { backgroundColor: string; color: string } | undefined {
  return getStatusBadgeStyle(trip.status);
}

export function getStatusLabel(trip: Trip, t: TFunction): string {
  return getStatusBadgeLabel(
    trip.status,
    t,
    t("trips.status.noStatus", { defaultValue: "N/A" }),
  );
}

export function getPriorityTone(priority: string): Tone {
  return PRIORITY_TONE_LOOKUP[priority] ?? "neutral";
}

export function getPriorityLabel(priority: string, t: TFunction): string {
  const key = PRIORITY_LABEL_LOOKUP[priority];
  return key ? t(key) : priority || "N/A";
}

export function formatTripDate(date: { _seconds: number; _nanoseconds: number } | string | undefined): string {
  if (!date) return "N/A";
  if (typeof date === "string") {
    const d = new Date(date);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  if (date._seconds) {
    const d = new Date(date._seconds * 1000);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return "N/A";
}

/** Subtítulo del viaje: su ruta o, si lo creó el Cargo Market, folio y cliente. */
export const tripSubtitle = (trip: Trip): string =>
  trip.route?.name ||
  (trip.cargoMarket
    ? `Cargo Market · ${trip.cargoMarket.folio}${trip.cargoMarket.shipperName ? ` · ${trip.cargoMarket.shipperName}` : ""}`
    : "N/A");
